import { Download, RefreshCw, Server } from 'lucide-react'
import { Button } from '../../../../components'
import type { AttendanceSourceInfo, HrmsBiometricStatusResponse } from '../types/hrmsAttendanceSettings.types'
import { SettingToggleRow } from './SettingToggleRow'

interface AttendanceSourceCardProps {
  source?: AttendanceSourceInfo
  biometricStatus?: HrmsBiometricStatusResponse
  biometricEnabled?: boolean
  manualExcelEnabled?: boolean
  onToggleBiometric?: (enabled: boolean) => void
  onToggleManualExcel?: (enabled: boolean) => void
  onDownloadTemplate: () => void
  disabled?: boolean
}

export function AttendanceSourceCard({
  source,
  biometricStatus,
  biometricEnabled = true,
  manualExcelEnabled = true,
  onToggleBiometric,
  onToggleManualExcel,
  onDownloadTemplate,
  disabled = false,
}: AttendanceSourceCardProps) {
  const isConnected =
    (biometricStatus?.status ?? '').toUpperCase() === 'CONNECTED' ||
    (biometricStatus?.status ?? '').toUpperCase() === 'ACTIVE'

  const statusLabel = biometricStatus?.status
    ? biometricStatus.status.charAt(0).toUpperCase() + biometricStatus.status.slice(1).toLowerCase()
    : 'Disconnected'

  const formatLastSync = (timestamp?: string | null): string => {
    if (!timestamp) return 'Never'
    try {
      const date = new Date(timestamp)
      if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      }
    } catch {
      // Fallback to raw string
    }
    return String(timestamp)
  }

  const lastSyncDisplay = biometricStatus?.lastSyncAt !== undefined
    ? formatLastSync(biometricStatus.lastSyncAt)
    : (source?.lastSyncedAt || 'Never')

  const activeDevices = biometricStatus?.activeDeviceCount ?? 0

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 min-w-0 font-sans">
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-slate-700 shrink-0">
            <Server className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900 truncate">Attendance Source</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Configure how employee attendance enters the HRMS.
            </p>
          </div>
        </div>

        <span className="text-[10px] uppercase font-semibold bg-teal-50 text-teal-800 rounded-full px-2.5 py-1 whitespace-nowrap shrink-0 border border-teal-200/60">
          AUTOMATED &amp; MANUAL
        </span>
      </div>

      {/* Inner card 1: Biometric Hardware Gateway */}
      <div className="mt-4 rounded-xl bg-gray-50 p-4 space-y-3 border border-slate-200/60">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-slate-900 text-sm">Biometric Hardware Gateway</span>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold border ${
                isConnected
                  ? 'bg-emerald-50 text-emerald-800 border-emerald-200/80'
                  : 'bg-slate-100 text-slate-700 border-slate-200'
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  isConnected ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'
                }`}
              />
              {statusLabel}
            </span>
          </div>

          <span className="text-xs text-slate-500 shrink-0">
            Active Devices: <strong className="font-semibold text-slate-700">{activeDevices}</strong>
          </span>
        </div>

        <p className="text-xs text-slate-500">
          Attendance punches are automatically synced from the biometric system across campus terminals.
        </p>

        <div className="rounded-lg bg-white px-3 py-2 border border-slate-200/60 flex items-center justify-between text-xs">
          <div className="flex items-center gap-2 text-slate-500 font-medium">
            <RefreshCw className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <span>Last Synchronization:</span>
          </div>
          <span className="font-bold text-slate-900">{lastSyncDisplay}</span>
        </div>

        {onToggleBiometric && (
          <div className="pt-2 border-t border-slate-200/60">
            <SettingToggleRow
              label="Biometric Hardware Sync"
              description="Enable automated punch synchronization from hardware terminals."
              checked={biometricEnabled}
              onChange={onToggleBiometric}
              disabled={disabled}
            />
          </div>
        )}
      </div>

      {/* Inner card 2: Manual Excel Import */}
      <div className="mt-3 rounded-xl bg-gray-50 p-4 space-y-3 border border-slate-200/60">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-slate-900 text-sm">Manual Excel Import</span>
            <span
              className={`inline-flex items-center gap-1 text-xs font-semibold ${
                manualExcelEnabled ? 'text-emerald-700' : 'text-slate-500'
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  manualExcelEnabled ? 'bg-emerald-500' : 'bg-slate-400'
                }`}
              />
              {manualExcelEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          <span className="text-xs font-bold text-slate-700 shrink-0">
            XLSX / CSV
          </span>
        </div>

        <p className="text-xs text-slate-500">
          HR/Admin can import manually maintained attendance records using standardized Excel templates during excursions and field camps.
        </p>

        {onToggleManualExcel && (
          <div className="pt-1 border-t border-slate-200/60">
            <SettingToggleRow
              label="Manual Excel Upload"
              description="Allow authorized administrators to upload bulk attendance spreadsheets."
              checked={manualExcelEnabled}
              onChange={onToggleManualExcel}
              disabled={disabled}
            />
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            icon={<Download className="h-4 w-4" />}
            onClick={onDownloadTemplate}
            className="bg-white hover:bg-slate-50 text-slate-700 border-slate-200 h-9 text-xs rounded-lg font-semibold shadow-xs shrink-0"
          >
            Download Template
          </Button>
        </div>
      </div>
    </div>
  )
}
